import { useCallback, useSyncExternalStore } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Node as DocumentNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';

import { t } from '../i18n';

export interface ContentsHeading {
  position: number;
  level: number;
  text: string;
}

export function topLevelHeadings(doc: DocumentNode): ContentsHeading[] {
  const headings: ContentsHeading[] = [];
  doc.forEach((node, position) => {
    const level: unknown = node.attrs['level'];
    if (node.type.name !== 'heading' || typeof level !== 'number' || level < 1 || level > 4) return;
    headings.push({ position, level, text: node.textContent.trim() });
  });
  return headings;
}

const EMPTY_HEADINGS: ContentsHeading[] = [];
export const contentsPluginKey = new PluginKey<ContentsHeading[]>('tableOfContents');

function navigateToHeading(editor: Editor, position: number): void {
  const node = editor.state.doc.nodeAt(position);
  const target = editor.view.nodeDOM(position);
  if (node?.type.name !== 'heading' || !(target instanceof HTMLElement)) return;
  editor.view.dispatch(
    editor.state.tr.setSelection(TextSelection.create(editor.state.doc, position + 1)),
  );
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'start' });
}

function ContentsView({ editor }: NodeViewProps): React.ReactElement {
  const subscribe = useCallback(
    (changed: () => void) => {
      editor.on('transaction', changed);
      return () => {
        editor.off('transaction', changed);
      };
    },
    [editor],
  );
  const snapshot = useCallback(
    () => contentsPluginKey.getState(editor.state) ?? EMPTY_HEADINGS,
    [editor],
  );
  const headings = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <NodeViewWrapper
      className="table-of-contents"
      contentEditable={false}
      data-table-of-contents=""
    >
      <nav aria-label={t('editor.tableOfContents')}>
        <p className="table-of-contents__title">{t('editor.tableOfContents.title')}</p>
        {headings.length ? (
          <ul className="table-of-contents__list">
            {headings.map((heading) => (
              <li key={heading.position}>
                <button
                  type="button"
                  className="nb table-of-contents__item"
                  style={{ paddingInlineStart: `calc(var(--space-4) * ${heading.level - 1})` }}
                  onClick={() => navigateToHeading(editor, heading.position)}
                >
                  {heading.text || t('page.untitled')}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="table-of-contents__empty">{t('editor.tableOfContents.empty')}</p>
        )}
      </nav>
    </NodeViewWrapper>
  );
}

export const TableOfContents = Node.create({
  name: 'tableOfContents',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML: () => [{ tag: '[data-table-of-contents]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'nav',
    mergeAttributes(HTMLAttributes, { 'data-table-of-contents': '' }),
  ],
  addNodeView: () => ReactNodeViewRenderer(ContentsView),
  addProseMirrorPlugins() {
    return [
      new Plugin<ContentsHeading[]>({
        key: contentsPluginKey,
        state: {
          init: (_, state) => topLevelHeadings(state.doc),
          apply: (transaction, previous) =>
            transaction.docChanged ? topLevelHeadings(transaction.doc) : previous,
        },
        props: {
          decorations(state) {
            const headings = contentsPluginKey.getState(state) ?? EMPTY_HEADINGS;
            return DecorationSet.create(
              state.doc,
              headings.flatMap((heading) => {
                const node = state.doc.nodeAt(heading.position);
                return node
                  ? [
                      Decoration.node(heading.position, heading.position + node.nodeSize, {
                        tabindex: '-1',
                      }),
                    ]
                  : [];
              }),
            );
          },
        },
      }),
    ];
  },
});
